import {Platform} from 'react-native';

// PermissionsAndroid only exists on Android; require lazily so web/iOS bundles
// never touch the native module.
const PermissionsAndroid = Platform.OS === 'android' ? require('react-native').PermissionsAndroid : null;

/**
 * Android 12 (API 31) introduced the runtime `BLUETOOTH_SCAN` / `BLUETOOTH_CONNECT`
 * permissions. `react-native-bluetooth-classic` calls
 * `BluetoothAdapter.cancelDiscovery()` (notably from its `onHostDestroy` teardown)
 * which requires `BLUETOOTH_SCAN`. When that permission is not granted the native
 * side throws `SecurityException: Need android.permission.BLUETOOTH_SCAN ...`,
 * crashing the app — most often on exit (Sentry HAPPY-APP-8 / HAPPY-APP-4).
 *
 * This is the API level at/after which those runtime permissions exist.
 * @type {number}
 */
const ANDROID_12_API_LEVEL = 31;

/**
 * Ensure the Android 12+ runtime Bluetooth permissions (`BLUETOOTH_SCAN` and
 * `BLUETOOTH_CONNECT`) are granted before starting/stopping device discovery.
 *
 * No-ops (returns `true`) on:
 *   - non-Android platforms (iOS/web have no such runtime permission), and
 *   - Android < 12, where these are install-time permissions only.
 *
 * Never throws — a denial or an unexpected error resolves to `false` so callers
 * can decide whether to proceed (bonded-device listing still works) instead of
 * crashing.
 *
 * @returns {Promise<boolean>} True when both permissions are granted (or N/A).
 */
export async function ensureBluetoothScanPermissions() {
    if (Platform.OS !== 'android' || !PermissionsAndroid) {
        return true;
    }
    if (Number(Platform.Version) < ANDROID_12_API_LEVEL) {
        // Pre-Android-12: BLUETOOTH_SCAN/CONNECT are install-time, not runtime.
        return true;
    }

    try {
        const results = await PermissionsAndroid.requestMultiple([
            PermissionsAndroid.PERMISSIONS.BLUETOOTH_SCAN,
            PermissionsAndroid.PERMISSIONS.BLUETOOTH_CONNECT,
        ]);

        const granted =
            results['android.permission.BLUETOOTH_SCAN'] === PermissionsAndroid.RESULTS.GRANTED &&
            results['android.permission.BLUETOOTH_CONNECT'] === PermissionsAndroid.RESULTS.GRANTED;

        if (!granted) {
            console.warn('[bluetoothPermissions] BLUETOOTH_SCAN/CONNECT not granted — Bluetooth discovery is disabled.');
        }
        return granted;
    } catch (error) {
        console.warn('[bluetoothPermissions] Failed to request Bluetooth runtime permissions:', error);
        return false;
    }
}

export default ensureBluetoothScanPermissions;
